/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { TShoppingTicket } from "../../utils/@types/data/ticket"
import { clockdown } from "../../utils/tb/timer"
import * as S from "./styled"

import Header from "../../components/Header"
import Footer from "../../components/Footer"
import Container from "../../components/Container"
import Feedback from "../../components/Feedback"

import { ReactComponent as CheckCircle } from "../../assets/icons/check_circle.svg"
import { ReactComponent as AttentionIcon } from "../../assets/icons/attention.svg"

import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import loadingAnimation from "../../assets/animations/loading"

import getStore from "../../store"
import { Api } from "../../api"
import { getOrderData } from "../../utils/tb/order"
import downloadTickets from "../../utils/pdf"
import { generateTicketID } from "../../utils/tb/qrcode"
import { TUser } from "../../utils/@types/data/user"
import { TEventData } from "../../utils/@types/data/event"
import { formatMoney } from "../../utils/tb/formatMoney"
import { getDatePeriod } from "../../utils/tb/getDatePeriod"

import pageTools from "../../utils/tb/pageTools/pix"
import { logCommunication } from "../../utils/logCommunication"
import { TPaymentSession } from "../../utils/@types/data/paymentSession"
import { usePaymentRejoin } from "../../hooks/usePaymentRejoin"
import OrderResume from "../../components/OrderResume"
import PostPaymentDelivery from "../../components/PostPaymentDelivery"
import { adoptOrderToken } from "../../utils/tb/orderToken"

const io = require("socket.io-client")

const socketUrl =
  process.env.REACT_APP_SOCKET_URL || "https://api.oitickets.com.br"

// Detecta erros de estoque vindos do `Api.post.purchase.sign` e devolve uma
// mensagem em pt-BR. Aceita tanto a resposta atual do backend
// (`error: "Saldo indisponível"`) quanto o formato futuro
// (`code: "STOCK_INSUFFICIENT"` com `details.available`).
const buildPixStockErrorMessage = (sign: {
  ok: false
  error?: string
  code?: string
  details?: any
}): string | undefined => {
  const code = sign.code ?? sign.error
  const isStock =
    code === "STOCK_INSUFFICIENT" ||
    /saldo indispon[ií]vel/i.test(sign.error ?? "") ||
    /estoque/i.test(sign.error ?? "")

  if (!isStock) return undefined

  const available = sign.details?.available
  if (typeof available === "number") {
    if (available <= 0)
      return "Este ingresso acabou de esgotar. Atualize seu carrinho e tente novamente."
    return `Restam apenas ${available} unidade${available === 1 ? "" : "s"} deste ingresso. Atualize seu carrinho e tente novamente.`
  }

  return "Não há mais ingressos disponíveis para essa compra. Atualize seu carrinho e tente novamente."
}

const PaymentPix = () => {
  const lctn = useLocation()
  const navigate = useNavigate()

  const eventData = sessionStorage.getItem("event")

  const event = eventData ? (JSON.parse(eventData) as TEventData) : null

  const { controllers } = getStore()

  const user = sessionStorage.getItem("user")
    ? (JSON.parse(sessionStorage.getItem("user") as string) as TUser)
    : null

  const [time, setTime] = useState("15:00")
  const [sid, setSid] = useState("")
  const [orderId, setOrderId] = useState<any>(null)
  const [externalReference, setExternalReference] = useState<any>(null)
  const orderIdRef = useRef<string | null>(null)

  const [buyedTickets, setBuyedTickets] = useState<TShoppingTicket[]>([])

  // Pedido aprovado: id (mesmo usado em purchaseInfo) e extref para o
  // fluxo de entrega por WhatsApp / link público.
  const [deliveryOrder, setDeliveryOrder] = useState({ id: "", extref: "" })
  const [requiringQr, setRequiringQr] = useState(false)

  const [totalOrderAmount, setTotalOrderAmount] = useState(0)
  const [qrError, setQrError] = useState("")
  const [qrCode, setQrCode] = useState("")
  const [qrCode64, setQrCode64] = useState("")
  const [feedback, setFeedback] = useState<any>({ visible: false, message: "" })

  const alreadyRequestingPaymentRef = useRef(false)

  const payedRef = useRef(false)
  const paymentSessionRef = useRef<TPaymentSession | null>(null)
  const socketRef = useRef<any>(null)

  const isOrderConfirmingRef = useRef(false)
  const isOrderConfirmedRef = useRef(false)
  const timerRef = useRef<any>(null)

  const [payed, setPayed] = useState(false)
  const [expired, setExpired] = useState(false)
  const [signError, setSignError] = useState<string | undefined>()

  const [socketForRejoin, setSocketForRejoin] = useState<any>(null)

  usePaymentRejoin({
    socket: socketForRejoin,
    active: !payed && !expired,
  })

  // ----- EMAIL -----

  const handleEmail = async (purchaseInfo: {
    status: string
    amount: number
    message: string
    sId: string
    transition_id: string
    time: string
  }) => {
    try {
      const req = await Api.get.purchaseInfo({
        eventId: event?.id as string,
        orderId: purchaseInfo.sId,
      })

      if (req.ok) {
        const external_reference = req.data.extref

        const parsedData = parsePurchaseInfo(req.data)

        setBuyedTickets(parsedData)

        setDeliveryOrder({
          id: purchaseInfo.sId,
          extref: external_reference,
        })

        const purchaseValue = pageTools.order.getOrderValue(
          lctn.state.tickets,
          lctn.state.buyer,
          lctn.state.taxTotal,
          sid,
          user as TUser,
          event?.dk as string,
          event?.id as string,
          event?.name as string,
          event?.eCommerce.chargeClient ?? false,
          {
            method: "pix",
            additionalTaxPix: event?.eCommerce.additionalTaxPix,
          },
        )

        const targetEmail = (lctn.state.buyer?.email as string) ?? ""
        const buyerName = lctn.state.buyer?.name as string

        // Só envia email se o usuário tiver preenchido o campo de email
        if (targetEmail && targetEmail.trim().length > 0) {
          const emailResult = await pageTools.email.sendEmail(
            event as TEventData,
            user as TUser,
            purchaseValue,
            lctn.state.tickets,
            {
              ...purchaseInfo,
              taxTotal: lctn.state.taxTotal as number,
              paymentMethod: "pix",
              transition_id: external_reference,
              time: new Date(req.data.products[0].date).toISOString(),
            },
            parsedData,
            targetEmail,
            buyerName,
          )
          // fire-and-forget log:
          logCommunication({
            event_id: String(event?.id ?? ""),
            order_id: String(purchaseInfo.sId ?? orderId ?? ""),
            buyer_name: buyerName,
            recipient: targetEmail,
            channel: "email",
            status: emailResult?.ok ? "success" : "failed",
          })
        }
      }
    } catch (error) {}
  }

  // ----- MERCADOPAGO -----

  const checkPendingPayment = useCallback(() => {
    const paymentSession = localStorage.getItem("paymentSession")
    const paymentToRecover: TPaymentSession | null = paymentSession
      ? JSON.parse(paymentSession)
      : null

    if (paymentToRecover) {
      const runnedTime = Math.floor(
        (new Date().getTime() - +paymentToRecover.paymentStartedAt) / 1000,
      )

      const remainingTime = 15 * 60 - runnedTime

      const isPaymentRecoverable = remainingTime > 0 // checkPaymentValidity

      if (isPaymentRecoverable) {
        // Sessão sem QR = geração falhou (o sign grava a sessão antes do
        // QR). Não há pagamento para acompanhar: mostra o retry em vez de
        // iniciar timer e polling.
        if (!paymentToRecover.qrCode) {
          setExternalReference(paymentToRecover.extref)
          setQrError("Não foi possível gerar o código PIX. Tente novamente.")
          return
        }

        setQrCode(paymentToRecover.qrCode)
        setQrCode64(paymentToRecover.qrCode64)
        setTotalOrderAmount(paymentToRecover.amount)
        runTimer(remainingTime)

        startPoolingOrderStatus()
      } else handleExpiredPayment()
    }
  }, [localStorage, setQrCode, setQrCode64])

  const handlePlugged = useCallback((socketId: string) => {
    setSid(socketId)
  }, [])

  const handleExpiredPayment = () => {
    setExpired(true)
    localStorage.setItem("expired", "true")
    localStorage.removeItem("paymentSession")

    if (socketRef.current) {
      try {
        socketRef.current.disconnect()
      } catch {}
      socketRef.current = null
    }

    let f = {
      state: "expired",
      visible: true,
      message: "O tempo para pagamento expirou.",
    }

    setFeedback(f)

    setTimeout(() => {
      setFeedback({ ...f, visible: false })
    }, 3500)
  }

  const checkPaymentValidity = (paymentToRecover: TPaymentSession) => {
    let isValid = true

    const runnedTime = Math.floor(
      (new Date().getTime() - +paymentToRecover.paymentStartedAt) / 1000,
    )

    const remainingTime = 15 * 60 - runnedTime

    isValid = remainingTime > 0

    return isValid
  }

  const startPoolingOrderStatus = useCallback(async (retries = 0) => {
    try {
      const paymentSession = localStorage.getItem("paymentSession")
      const paymentToRecover: TPaymentSession | null = paymentSession
        ? JSON.parse(paymentSession)
        : null

      if (paymentToRecover) {
        const isValid = checkPaymentValidity(paymentToRecover)

        if (!isValid) {
          handleExpiredPayment()
          return
        }

        const req = await Api.get.purchaseInfo({
          eventId: event?.id as string,
          orderId: paymentToRecover.socketId,
        })

        if (req.ok) {
          const { payments } = req.data

          const status =
            payments.length === 0 ? false : payments[0].transition_code !== null

          if (status) {
            const purchase = await confirmPurchase(
              paymentToRecover.socketId,
              paymentToRecover.paymentId,
            )

            let f = {
              state: "validado",
              visible: false,
              message: "Pagamento realizado com sucesso.",
            }

            if (purchase.ok) {
              f.visible = true

              setFeedback(f)

              setPayed(true)
              payedRef.current = true
              orderIdRef.current = paymentToRecover.socketId
              localStorage.removeItem("paymentSession")
              paymentSessionRef.current = null

              if (socketRef.current) {
                try {
                  socketRef.current.disconnect()
                } catch {}
                socketRef.current = null
              }

              localStorage.setItem("payed", "true")

              handleEmail({
                amount: paymentToRecover.amount,
                message: "",
                sId: paymentToRecover.socketId,
                status: "validado",
                time: new Date().toISOString(),
                transition_id: paymentToRecover.extref,
              })

              setTimeout(() => {
                setFeedback({ ...f, visible: false })
              }, 3500)
            }
          } else {
            const isPayed = localStorage.getItem("payed") === "true"
            const isPaymentPage = window.location.href.endsWith("pix")

            if (isPaymentPage && !isPayed) {
              setTimeout(() => {
                startPoolingOrderStatus(retries)
              }, 4000)

              return
            }
          }
        }
      } else {
        if (retries <= 3) {
          setTimeout(() => {
            startPoolingOrderStatus(retries + 1)
          }, 3000)
        }
      }
    } catch (error) {}
  }, [])

  const startSocket = async () => {
    if (payed) return
    if (socketRef.current) return

    const socket = instanceSocket()
    if (!socket) return

    socketRef.current = socket

    // Caso ainda não exista uma sessão de pagamento, o `plugged` traz o socketId
    // que será usado como `cCode` na criação do pagamento. Em sessões já
    // existentes (reload no meio do PIX), o `usePaymentRejoin` cuida de
    // reentrar na room antiga via `rejoinPaymentSession`.
    const hasOngoing = !!localStorage.getItem("paymentSession")
    if (!hasOngoing) {
      socket.on("plugged", (socketData: any) => {
        handlePlugged(socketData)
      })
    }

    socket.on("orderUpdate", (payload: any) => {
      if (payedRef.current) return
      const status = payload?.paymentStatus ?? payload?.status
      if (status === "approved" || status === "validado") {
        // Acelera a confirmação via REST (já guardado por `isOrderConfirmingRef`).
        startPoolingOrderStatus(0)
      } else if (status === "rejected") {
        handleExpiredPayment()
      }
    })

    socket.on("orderCanceled", () => {
      if (payedRef.current) return
      handleExpiredPayment()
    })

    setSocketForRejoin(socket)
  }

  const storeDataIntoPaymentSession = (
    qrCode: string,
    qrCode64: string,
    paymentAmount: number,
  ) => {
    const paymentSession = localStorage.getItem("paymentSession")
    const paymentToRecover: TPaymentSession | null = paymentSession
      ? JSON.parse(paymentSession)
      : null

    if (paymentToRecover) {
      if (paymentToRecover.qrCode.length === 0) {
        const newData: TPaymentSession = {
          ...paymentToRecover,
          qrCode,
          qrCode64,
          amount: paymentAmount,
        }

        paymentSessionRef.current = newData
        localStorage.setItem("paymentSession", JSON.stringify(newData))
      }
    }
  }

  const getQR = async (socketId?: string, extref?: string): Promise<boolean> => {
    let generated = false

    try {
      if (!requiringQr && (socketId || sid) && (extref || externalReference)) {
        setRequiringQr(true)
        setQrError("")

        const external_reference = extref ?? externalReference

        const orderData = getOrderData({
          tickets: lctn.state.tickets,
          buyer: lctn.state.buyer,
          taxTotal: !Number.isNaN(lctn.state.taxTotal)
            ? Number(lctn.state.taxTotal)
            : 0,
          sid: socketId ?? sid,
          external_reference: external_reference,
          user: user as TUser,
          dk: (event as TEventData).dk,
          eventId: (event as TEventData).id,
          eventName: (event as TEventData).name,
          chargeClient: (event as TEventData).eCommerce.chargeClient,
          additionalTaxPix: (event as TEventData).eCommerce.additionalTaxPix,
        })

        if (orderData) {
          const qr = await Api.get.qrcode({ order: orderData } as any)

          if (qr.ok) {
            setQrCode(qr.data.qrcodes.code)
            setQrCode64(qr.data.qrcodes.base64)
            storeDataIntoPaymentSession(
              qr.data.qrcodes.code,
              qr.data.qrcodes.base64,
              orderData.transaction_amount,
            )
            runTimer()
            generated = true
          } else {
            setQrError(
              qr.error ||
                "Não foi possível gerar o código PIX. Tente novamente.",
            )
          }
        }
      }
    } catch (error) {
      setQrError("Não foi possível gerar o código PIX. Tente novamente.")
    }

    setRequiringQr(false)

    return generated
  }

  const startPurchase = async () => {
    if (user && user.fone) {
      if (!alreadyRequestingPaymentRef.current) {
        const extref = await signPurchase(sid)
        const generated = await getQR(sid, extref)

        // Sem QR não existe pagamento no MP para acompanhar — o polling só
        // começa quando o código for gerado (aqui ou no "Tentar novamente").
        if (!generated) return
      }

      startPoolingOrderStatus()
    } else {
      returnPage()
      return
    }
  }

  const checkIsRefreshAfterPayment = async () => {
    let savedPayment = false

    await new Promise((resolve) =>
      setTimeout(() => {
        const localPayed = localStorage.getItem("payed")
        savedPayment = localPayed
          ? localStorage.getItem("payed") === "true"
          : false
        resolve(true)
      }, 500),
    )

    return savedPayment
  }

  const ignite = useCallback(async () => {
    const pendingPayment: TPaymentSession | null =
      (paymentSessionRef.current ?? localStorage.getItem("paymentSession"))
        ? JSON.parse(localStorage.getItem("paymentSession") ?? "{}")
        : null

    const savedPayment = localStorage.getItem("payed")

    const wasExpired = localStorage.getItem("expired") === "true"

    const isPayed =
      (payedRef.current ?? savedPayment) ? savedPayment === "true" : payed

    const hasOngoingPurchase = !!pendingPayment

    if (wasExpired) {
      handleExpiredPayment()
    } else if (!isPayed) {
      // A sessão gravada pelo sign desta própria página não é "pendente":
      // o startPurchase já cuida dela.
      if (hasOngoingPurchase) {
        if (!alreadyRequestingPaymentRef.current) checkPendingPayment()
      } else {
        const alreadyPayed = await checkIsRefreshAfterPayment()

        if (alreadyPayed) {
          goToMyTickets()
          return
        } else {
          if (
            sid !== "" &&
            expired === false &&
            !alreadyRequestingPaymentRef.current
          ) {
            startPurchase()
            alreadyRequestingPaymentRef.current = true
          }
        }
      }
    }
  }, [
    sid,
    paymentSessionRef.current,
    payedRef.current,
    alreadyRequestingPaymentRef.current,
    payed,
    expired,
  ])

  useEffect(() => {
    ignite()
  }, [ignite])

  // ----- PAGE -----

  const copyQRToClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(qrCode).then(() => {
          let f = {
            state: "info",
            visible: true,
            message: "Código copiado",
          }

          setFeedback(f)

          setTimeout(() => {
            setFeedback({ ...f, visible: false })
          }, 2000)
        })
      } else {
        const textarea = document.createElement("textarea")

        textarea.value = qrCode

        // Torna o elemento invisível
        textarea.style.position = "fixed"
        textarea.style.zIndex = "-1"
        textarea.style.visibility = "invisible"
        textarea.style.opacity = "0"

        document.body.appendChild(textarea)

        textarea.select()

        try {
          // Executa o comando de cópia
          const successful = document.execCommand("copy")
          const msg = successful
            ? "Texto copiado com sucesso!"
            : "Falha ao copiar o texto."
          alert(msg)
        } catch (err) {
          alert("Erro ao copiar o código. Tente novamente mais tarde")
        }

        textarea.remove()
      }
    } catch (error) {
      alert(error)
    }
  }

  const returnPage = () => {
    navigate(-1)
  }

  const instanceSocket = useCallback(() => {
    const socket = io(socketUrl, {
      autoConnect: true,
      closeOnBeforeunload: false,
      path: "",
      secure: false,
    })

    return socket
  }, [])

  const restartTimer = () => {
    const savedPayed = localStorage.getItem("payed") === "true" || payed

    if (!savedPayed) {
      setQrCode("")
      setQrCode64("")
      setTime("15:00")

      // getQR()
    }
  }

  const runTimer = (seconds = 900) => {
    // Limpa o timer anterior se existir
    if (timerRef.current) {
      timerRef.current.stop()
      timerRef.current = null
    }

    const timer: any = clockdown(
      seconds,
      restartTimer,
      (newTime?: string | null) => {
        if (newTime) setTime(newTime)
      },
    )

    timerRef.current = timer
    timer.start()
  }

  const loadEventData = useCallback(async () => {
    if (event) {
      try {
        const req = await Api.get.eventInfo({ eventId: event?.id })

        if (req.ok) {
          const data = req.data
          sessionStorage.setItem("event", JSON.stringify(data))
          controllers.event.setData(data)
        } else {
          alert("Erro ao carregar as informações do evento")
          navigate("/eventSelect")
        }
      } catch (error) {
        alert("Erro ao carregar as informações do evento")
        navigate("/eventSelect")
      }
    } else navigate("/eventSelect")
  }, [])

  const signPurchase = useCallback(
    async (sId: string): Promise<string | undefined> => {
      let extref

      if (sId && event && event?.id && user) {
        const orderData = getOrderData({
          tickets: lctn.state.tickets,
          buyer: lctn.state.buyer,
          taxTotal: !Number.isNaN(+lctn.state.taxTotal)
            ? Number(lctn.state.taxTotal)
            : 0,
          sid: sId,
          user: user,
          dk: event.dk,
          eventId: event.id,
          eventName: event.name,
          chargeClient: event.eCommerce.chargeClient,
        })

        const sign = await Api.post.purchase.sign({
          user_fone: user?.fone,
          event_id: event?.id as string,
          order_id: sId,
          buyer_name: lctn.state.buyer ? lctn.state.buyer.name : "",
          buyer_email: lctn.state.buyer ? lctn.state.buyer.email : "",
          products: (lctn.state.tickets as any[]).map((i: any) => ({
            ...i,
            charge_client: Boolean(event?.eCommerce.chargeClient) ? 1 : 0,
          })),
          payments: [
            {
              payment_type: "pix",
              price: orderData?.transaction_amount as number,
              transitionCode: null,
              transitionId: null,
            },
          ],
        })

        if (sign.ok && sign.data.success) {
          // Antes do loadPurchaseData: o getPurchase precisa da sessão.
          adoptOrderToken(sign.data.order_id, sign.data.order_token, {
            asSession: !user || user.guest === true,
          })
          extref = sign.data.extref
          const order_id = sign.data.order_id
          setOrderId(order_id)
          setExternalReference(extref)

          const localPaymentSession = localStorage.getItem("paymentSession")

          if (!localPaymentSession) {
            const paymentSession: TPaymentSession = {
              extref: extref,
              paymentId: sign.data.order_id,
              socketId: sId,
              qrCode: "",
              qrCode64: "",
              paymentStartedAt: new Date().getTime().toString(),
              amount: orderData?.transaction_amount as number,
            }

            localStorage.setItem(
              "paymentSession",
              JSON.stringify(paymentSession),
            )
          }

          loadPurchaseData(sign.data.order_id)
        } else if (!sign.ok) {
          const stockMsg = buildPixStockErrorMessage(sign)
          setSignError(
            stockMsg ??
              "Não foi possível iniciar seu pedido. Por favor, tente novamente.",
          )
        }
      }

      return extref
    },
    [],
  )

  const parsePurchaseInfo = (p: any) => {
    let data: any = []

    let pdfTickets: any[] = []

    const tickets = p.products as any[]

    const external_reference = p.extref
    const buyer_name = p.buyer?.name || p.buyer_name

    tickets.forEach((t, k) => {
      const tid = generateTicketID(
        false,
        "ecommerce",
        t.opuid,
        event?.oid as number,
        event?.dbName as string,
      )

      const dt = {
        id: t.id,
        name: t.name,
        batch_name: t.batch_name,
        is_mesa: t.is_mesa === true,
        event_name: event?.name as string,
        qr_data: t.qr_data,
        qr_label: t.qr_label,
        combo_id: t.combo_id,
        combo_name: t.combo_name,
        qr_TID: tid,
        order_id: sid,
        date: new Date(t.date).toISOString(),
        image: null,
        quantity: t.quantity,
        price_unit: t.price_unit,
        tax_value: t.tax_value,
        ticket_name: (t as any).ticket_name,
        user_name: (t as any).user_name,
        buyer_name: buyer_name,
        TRN: external_reference,
        payment_type: t.payment_type,
      }

      pdfTickets.push(dt)
    })

    data = pdfTickets

    return data
  }

  const loadPurchaseData = async (orderId: string) => {
    // place data
    if (event) {
      try {
        const p = await Api.get.purchaseInfo({ eventId: event.id, orderId })

        if (p.ok && p.data.id) {
          const parsedData = parsePurchaseInfo(p.data)

          setBuyedTickets(parsedData)
          setDeliveryOrder({ id: orderId, extref: p.data.extref })
        }
      } catch (error) {}
    }
  }

  const confirmPurchase = async (sId: string, pcode: string) => {
    if (!isOrderConfirmingRef.current && !isOrderConfirmedRef.current) {
      isOrderConfirmingRef.current = true

      const confirmation = await Api.post.purchase.confirm({
        order_id: sId,
        order_number: orderId,
        payment_code: pcode,
      })

      if (confirmation.ok && confirmation.data.success) {
        isOrderConfirmedRef.current = true
      }

      isOrderConfirmingRef.current = false

      return confirmation
    } else {
      return { ok: false, data: { success: false } }
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0 })

    try {
      if (!lctn.state?.tickets) keepShopping()
      else {
        loadEventData()
        startSocket()
      }
    } catch (error) {}

    // Cleanup: limpa o timer e o socket quando o componente desmonta
    return () => {
      if (timerRef.current) {
        timerRef.current.stop()
        timerRef.current = null
      }
      if (socketRef.current) {
        try {
          socketRef.current.disconnect()
        } catch {}
        socketRef.current = null
      }
    }
  }, [])

  const handleDownload = async () => {
    if (event) await downloadTickets(event, buyedTickets, true)
  }

  /*
  const handleShare = async () => {
    try {
      if (event) {
        const file = await downloadTickets(event, buyedTickets)

        if (file instanceof File) {
          const data = {
            title: `Meus Ingressos para ${event.name}`,
            files: [file],
          }

          if (navigator.canShare && navigator.canShare(data)) {
            navigator.share(data)
          }
        }
      }
    } catch (error) {}
  }
  */

  const keepShopping = () => {
    navigate("/", { replace: true, state: { backRoute: "/eventSelect" } })
  }

  const goToMyTickets = () => {
    navigate("/mytickets", { replace: true, state: { backRoute: "/" } })
  }

  // ----- CALCULATE ORDER TOTAL AMOUNT -----

  useEffect(() => {
    if (!lctn.state?.tickets) {
      keepShopping()
      return
    }

    if (totalOrderAmount === 0) {
      if (
        lctn.state.tickets !== undefined &&
        lctn.state.buyer !== undefined &&
        lctn.state.taxTotal !== undefined &&
        sid !== "" &&
        user &&
        event &&
        event.dk &&
        event.id
      ) {
        const orderValueAmount = pageTools.order.getOrderValue(
          lctn.state.tickets,
          lctn.state.buyer,
          lctn.state.taxTotal,
          sid,
          user as TUser,
          event?.dk as string,
          event?.id as string,
          event?.name as string,
          event?.eCommerce.chargeClient,
          {
            method: "pix",
            additionalTaxPix: event?.eCommerce.additionalTaxPix,
          },
        )

        if (!Number.isNaN(orderValueAmount))
          setTotalOrderAmount(orderValueAmount)
      }
    }
  }, [lctn.state, sid, user, event, totalOrderAmount])

  return (
    <S.Page>
      <Feedback data={feedback} />
      <Header />

      <Container fullHeight={true}>
        <S.Main $justifyCenter={payed || expired || !!signError}>
          <S.Block $k={2} $flat={payed}>
            {/* Sign error (estoque ou outro motivo) */}
            {signError && !payed && !expired && (
              <>
                <S.Feedback $k={3}>
                  <span>Ingresso indisponível</span>
                  <AttentionIcon />
                </S.Feedback>
                <S.FeedbackIntructions $k={4}>
                  <span>{signError}</span>
                </S.FeedbackIntructions>
                <S.PayedArea>
                  <S.Button
                    $outlined={true}
                    $content={true}
                    onClick={keepShopping}
                  >
                    Iniciar nova compra
                  </S.Button>
                </S.PayedArea>
              </>
            )}

            {/* Expired */}
            {expired && (
              <S.Feedback $k={3}>
                <span>Tempo esgotado!</span>
                <CheckCircle />
              </S.Feedback>
            )}

            {expired && (
              <S.FeedbackIntructions $k={4}>
                <span>
                  O tempo para pagamento expirou. Por favor, inicie uma nova
                  compra.
                </span>
              </S.FeedbackIntructions>
            )}

            {expired && (
              <S.PayedArea>
                <S.Button
                  $outlined={true}
                  $content={true}
                  onClick={keepShopping}
                >
                  Iniciar nova compra
                </S.Button>
              </S.PayedArea>
            )}

            {!payed && !expired && !signError && (
              <S.BlockTitle $k={4}>
                Agora só falta concluir o seu Pix.
              </S.BlockTitle>
            )}

            {/* Payed */}
            {payed && (
              <PostPaymentDelivery
                eventId={String(event?.id ?? "")}
                eventName={event?.name ?? ""}
                eventDate={
                  event?.date_ini
                    ? getDatePeriod(event.date_ini, event.date_end)
                    : undefined
                }
                orderId={deliveryOrder.id}
                extref={deliveryOrder.extref}
                quantity={
                  (lctn.state?.tickets as any[] | undefined)?.reduce(
                    (sum, t) => sum + (Number(t.quantity) || 1),
                    0,
                  ) ?? buyedTickets.length
                }
                buyerName={lctn.state?.buyer?.name}
                buyerPhone={lctn.state?.buyer?.phone ?? ""}
                buyerEmail={lctn.state?.buyer?.email}
                onDownload={handleDownload}
                onSeeMyTickets={goToMyTickets}
              />
            )}

            {!expired && !payed && !signError && (
              <S.PixArea>
                <S.PixInstructions>
                  <span>
                    Utilize o <strong>código</strong> a seguir para realizar o
                    pagamento pelo <strong>aplicativo do seu banco</strong>
                  </span>
                </S.PixInstructions>
                <S.QR>
                  {qrCode ? (
                    <>
                      {qrCode && qrCode64 && (
                        <img
                          src={`data:image/jpeg;base64,${qrCode64}`}
                          alt=""
                        />
                      )}

                      <span>{formatMoney(totalOrderAmount, true)}</span>

                      <S.Button onClick={copyQRToClipboard}>
                        Copiar código
                      </S.Button>
                    </>
                  ) : qrError ? (
                    <>
                      <span>{qrError}</span>
                      <S.Button
                        onClick={async () => {
                          // Após reload o sid é novo; o pedido assinado usa o
                          // socketId/extref gravados na sessão.
                          const raw = localStorage.getItem("paymentSession")
                          const session: TPaymentSession | null = raw
                            ? JSON.parse(raw)
                            : null
                          if (await getQR(session?.socketId, session?.extref))
                            startPoolingOrderStatus()
                        }}
                      >
                        Tentar novamente
                      </S.Button>
                    </>
                  ) : (
                    <div style={{ width: 256 }}>
                      <DotLottieReact
                        data={loadingAnimation}
                        loop
                        autoplay
                        width={"100%"}
                      />
                    </div>
                  )}
                </S.QR>
                <S.PixTime>
                  <div
                    style={{
                      display: "flow",
                      margin: "0 auto 12px auto",
                      padding: "6px 14px",
                      borderRadius: 6,
                      backgroundColor: "#fbd61840",
                      width: "fit-content",
                    }}
                  >
                    <AttentionIcon
                      style={{
                        minWidth: 24,
                        maxWidth: 24,
                        margin: "0px 12px -4px 0px",
                        display: "inline",
                      }}
                      width={24}
                      height={24}
                    />
                    <span>Por favor, não feche nem atualize a página</span>
                  </div>
                  <span>
                    Você tem <strong>{time}</strong> para realizar o pagamento
                  </span>
                  <span>
                    Após o pagamento, clique em <strong>Meus ingressos</strong>{" "}
                    para ver seus ingressos
                  </span>
                </S.PixTime>
              </S.PixArea>
            )}
          </S.Block>

          {!payed && !signError && (
            <OrderResume
              hideEventData={true}
              onlyPurchasingItems={true}
              fitContainer={true}
              reverse={true}
              paymentMethod="pix"
              ticketsList={
                lctn.state.disposalTickets.length > 0
                  ? lctn.state.disposalTickets
                  : lctn.state.tickets
              }
            />
          )}
        </S.Main>
      </Container>

      <Footer />
    </S.Page>
  )
}

export default PaymentPix
